"use client"

import { catchError, type ErrorInfo } from "next/error"

/**
 * Component-level boundary for CMS blocks, payment widgets and third-party
 * embeds: a broken subtree collapses to nothing instead of taking the page down
 * with it. Silent in production, loud enough to notice in development.
 *
 * Built on Next 16.3's `catchError` rather than a hand-rolled class boundary.
 * The reason is `notFound()` / `redirect()`: those throw a special digest that a
 * plain `getDerivedStateFromError` happily swallows, so a Server Component child
 * asking for a redirect would silently render nothing here. `catchError`
 * re-throws router errors so they reach the boundary that owns them.
 *
 * `errorInfo` also carries `retry()` (re-fetches the segment) and `reset()`.
 * Neither is wired up — these boundaries are intentionally invisible. Giving the
 * payment ones a visible recovery affordance is a deliberate follow-up.
 */
const ErrorBoundaryFallback = (
  _props: Record<never, never>,
  { error }: ErrorInfo
) => {
  console.error("Uncaught error:", error)

  if (process.env.NODE_ENV === "development") {
    return <pre>COMPONENT ERROR</pre>
  }

  return null
}

export const ErrorBoundary = catchError(ErrorBoundaryFallback)
